import React, { useState, useRef, useEffect, useCallback } from "react";

/**
 * CAD Command Line
 *
 * Supports:
 *   WALL / W        — enter wall draw mode
 *   LINE <x1,y1> <x2,y2> — draw a wall from coords
 *   CIRCLE          — (future)
 *   LAYER           — list layers
 *   LAYER SHOW <id> — show layer
 *   LAYER HIDE <id> — hide layer
 *   ZOOM <factor>   — set zoom
 *   MOVE / M        — enter move mode
 *   SELECT / SEL    — enter select mode
 *   ROOM / R        — enter room label mode
 *   OPENING / O     — enter opening mode
 *   DIM / D         — enter dimension mode
 *   ANNOTATE / A    — enter annotation mode
 *   HEAL / H        — run heal
 *   CLEAR / C       — clear canvas
 *   UNDO / U        — undo
 *   REDO            — redo
 *   HELP / ?        — show help
 */

const COMMANDS = {
  W:        { full: "WALL",      desc: "Draw wall mode" },
  WALL:     { full: "WALL",      desc: "Draw wall mode" },
  S:        { full: "SELECT",    desc: "Select/drag mode" },
  SEL:      { full: "SELECT",    desc: "Select/drag mode" },
  SELECT:   { full: "SELECT",    desc: "Select/drag mode" },
  M:        { full: "MOVE",      desc: "Move node mode" },
  MOVE:     { full: "MOVE",      desc: "Move node mode" },
  R:        { full: "ROOM",      desc: "Label room mode" },
  ROOM:     { full: "ROOM",      desc: "Label room mode" },
  O:        { full: "OPENING",   desc: "Add opening mode" },
  OPENING:  { full: "OPENING",   desc: "Add opening mode" },
  A:        { full: "ANNOTATE",  desc: "Add text annotation" },
  ANNOTATE: { full: "ANNOTATE",  desc: "Add text annotation" },
  X:        { full: "DIM",       desc: "Place dimension" },
  DIM:      { full: "DIM",       desc: "Place dimension" },
  AD:       { full: "ADIM",      desc: "Angular dim (3-click: vertex, ptA, ptB)" },
  ADIM:     { full: "ADIM",      desc: "Angular dimension mode" },
  H:        { full: "HEAL",      desc: "Auto-heal graph" },
  HEAL:     { full: "HEAL",      desc: "Auto-heal graph" },
  C:        { full: "CLEAR",     desc: "Clear canvas" },
  CLEAR:    { full: "CLEAR",     desc: "Clear canvas" },
  U:        { full: "UNDO",      desc: "Undo last action" },
  UNDO:     { full: "UNDO",      desc: "Undo last action" },
  REDO:     { full: "REDO",      desc: "Redo" },
  DXF:      { full: "DXF",       desc: "Export DXF file" },
  EXPORT:   { full: "DXF",       desc: "Export DXF file" },
  BOQ:      { full: "BOQ",       desc: "Export full BOQ as CSV" },
  SCHEDULE: { full: "SCHEDULE",  desc: "Export schedule: SCHEDULE walls|openings|rooms" },
  LSTATE:   { full: "LSTATE",    desc: "Layer state: LSTATE SAVE <name> | RESTORE <name> | LIST" },
  CON:      { full: "CON",       desc: "Constraint: CON LOCK|PERP|PAR|H|V" },
  LOCK:     { full: "CON",       desc: "Lock wall length: LOCK [mm]" },
  PERP:     { full: "CON",       desc: "Make 2 selected walls perpendicular" },
  TR:       { full: "TRIM",      desc: "Trim mode (K key) — click edge, then cutting edge" },
  TRIM:     { full: "TRIM",      desc: "Trim edge at intersection" },
  VIEW3D:   { full: "VIEW3D",    desc: "Toggle 3D preview (3 key)" },
  C:        { full: "CIRCLE",    desc: "Circle mode — click center, click radius point" },
  CIRCLE:   { full: "CIRCLE",    desc: "CIRCLE cx,cy r  or activate circle mode" },
  ARC:      { full: "ARC",       desc: "Arc mode — 3 clicks: start, mid, end" },
  SPL:      { full: "SPLINE",    desc: "Spline mode — click points, Enter to finish" },
  SPLINE:   { full: "SPLINE",    desc: "Spline through control points" },
  PL:       { full: "POLYLINE",  desc: "Polyline mode — click points, Enter to finish" },
  POLYLINE: { full: "POLYLINE",  desc: "Connected line segments" },
  HATCH:    { full: "HATCH",     desc: "HATCH pattern: ansi31|wood|brick|concrete|glass|metal" },
  MODE:     { full: "MODE",      desc: "MODE architectural|mechanical|furniture|electrical|civil|schematic" },
  // ── v7: Edit tools ──────────────────────────────────────────────────────
  F:        { full: "FILLET",    desc: "FILLET r — round corner between 2 selected edges" },
  FILLET:   { full: "FILLET",    desc: "FILLET r — round corner at given radius" },
  CHA:      { full: "CHAMFER",   desc: "CHAMFER d1 d2 — cut corner between 2 selected edges" },
  CHAMFER:  { full: "CHAMFER",   desc: "CHAMFER d — cut corner at distance d" },
  EX:       { full: "EXTEND",    desc: "EXTEND — extend edge to meet boundary edge" },
  EXTEND:   { full: "EXTEND",    desc: "Extend mode: click edge to extend, click boundary" },
  POL:      { full: "POLYGON",   desc: "POLYGON sides r — regular polygon at snap point" },
  POLYGON:  { full: "POLYGON",   desc: "POLYGON 6 500 — hexagon radius 500mm" },
  BR:       { full: "BREAK",     desc: "BREAK — split an edge at click point" },
  BREAK:    { full: "BREAK",     desc: "Break/split edge at a point" },
  // ── v7: Line properties ─────────────────────────────────────────────────
  LT:       { full: "LINETYPE",  desc: "LINETYPE continuous|dashed|dotted|dashdot|hidden" },
  LINETYPE: { full: "LINETYPE",  desc: "Set linetype of selected entity" },
  LW:       { full: "LINEWEIGHT",desc: "LW 0.25 — set lineweight in mm (0.18/0.25/0.35/0.5/0.7/1.0)" },
  LINEWEIGHT:{ full:"LINEWEIGHT",desc: "Set lineweight of selected entity" },
  // ── v7: CNC ─────────────────────────────────────────────────────────────
  CNC:      { full: "CNC",       desc: "CNC router|laser|plasma — export G-code for CNC machine" },
  GCODE:    { full: "CNC",       desc: "Export G-code: GCODE router|laser" },
  CUTLIST:  { full: "CUTLIST",   desc: "Export cut list CSV for modular panels" },
  NEST:     { full: "NEST",      desc: "NEST 8x4 — nest panels onto sheet (8x4, 6x3, 2400x1200)" },
  PANEL:    { full: "PANEL",     desc: "PANEL base|wall|tall|drawer|tv_unit w h — insert cabinet module" },
  // ── Blocks ──────────────────────────────────────────────────────────────
  BLOCK:    { full: "BLOCK",     desc: "BLOCK name — define block from selection" },
  B:        { full: "BLOCK",     desc: "Define block from current selection" },
  INSERT:   { full: "INSERT",    desc: "INSERT defId — place block at cursor" },
  I:        { full: "INSERT",    desc: "Insert block" },
  EXPLODE:  { full: "EXPLODE",   desc: "EXPLODE refId — explode block to raw geometry" },
  X:        { full: "EXPLODE",   desc: "Explode selected block reference" },
  // ── AI ───────────────────────────────────────────────────────────────────
  AI:       { full: "AI",        desc: "AI prompt — draw from natural language. e.g. AI 3BHK flat 1200sqft" },
  DRAW:     { full: "AI",        desc: "AI DRAW prompt — alias for AI command" },
  // ── Floor ────────────────────────────────────────────────────────────────
  FLOOR:    { full: "FLOOR",     desc: "FLOOR n — switch to floor n (0=ground, 1=first...)" },
  PAR:      { full: "CON",       desc: "Make 2 selected walls parallel" },
  UNCONSTRAIN: { full: "CON",    desc: "Remove constraints from selected edge" },
  LAYER:    { full: "LAYER",     desc: "Layer commands: LAYER SHOW/HIDE <id>" },
  ZOOM:     { full: "ZOOM",      desc: "Set zoom: ZOOM 2" },
  HELP:     { full: "HELP",      desc: "Show all commands" },
  "?":      { full: "HELP",      desc: "Show all commands" },
  LINE:     { full: "LINE",      desc: "Draw line: LINE x1,y1 x2,y2 (mm)" },
  RECT:     { full: "RECT",      desc: "Draw room rectangle: RECT x,y w,h (mm)" },
  GRID:     { full: "GRID",      desc: "Add grid line: GRID X label or GRID Y label at cursor" },
  OF:       { full: "OFFSET",    desc: "Offset: OF <dist_mm> — then click an edge" },
  CP:       { full: "COPY",      desc: "Copy edges: CP <dx,dy>" },
  COPY:     { full: "COPY",      desc: "Copy selection: COPY dx,dy" },
  RO:       { full: "ROTATE",    desc: "Rotate selection: RO <angle>" },
  ROTATE:   { full: "ROTATE",    desc: "Rotate: ROTATE <angle_deg>" },
  SC:       { full: "SCALE",     desc: "Scale selection: SC <factor>" },
  SCALE:    { full: "SCALE",     desc: "Scale: SCALE <factor>" },
  MI:       { full: "MIRROR",    desc: "Mirror mode: MI (pick 2 pts for axis)" },
  MIRROR:   { full: "MIRROR",    desc: "Mirror selection" },
  AR:       { full: "ARRAY",     desc: "Rect array: AR <cols> <rows> <csp> <rsp>" },
  ARRAY:    { full: "ARRAY",     desc: "Rectangular array" },
  AP:       { full: "ARRAYPOLAR",desc: "Polar array: AP <count> <arc_deg>" },
  ARRAYPOLAR:{ full:"ARRAYPOLAR",desc: "Polar array" },
  OFFSET:   { full: "OFFSET",    desc: "Offset: OFFSET <dist_mm> — then click an edge" },
};

export default function CommandLine({ onCommand, snapPoint, mode, zoom }) {
  const [input,    setInput]    = useState("");
  const [history,  setHistory]  = useState([
    { type: "info", text: "J.A.D.E — Joint Aided Design Engine · type HELP or ? for commands" },
  ]);
  const [histIdx,  setHistIdx]  = useState(-1);
  const [cmdHist,  setCmdHist]  = useState([]);
  const [suggest,  setSuggest]  = useState([]);
  const inputRef = useRef(null);
  const logRef   = useRef(null);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [history]);

  const log = useCallback((text, type = "info") => {
    setHistory(h => [...h.slice(-80), { type, text }]);
  }, []);

  const handleSuggest = useCallback((val) => {
    const upper = val.toUpperCase().trim();
    if (!upper) { setSuggest([]); return; }
    const matches = Object.keys(COMMANDS).filter(k => k.startsWith(upper) && k !== upper);
    setSuggest(matches.slice(0, 5));
  }, []);

  const execute = useCallback((raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    setCmdHist(h => [trimmed, ...h.slice(0, 49)]);
    setHistIdx(-1);
    log(`> ${trimmed}`, "cmd");

    const parts = trimmed.toUpperCase().split(/\s+/);
    const cmd   = COMMANDS[parts[0]];

    if (!cmd) {
      log(`Unknown command: "${parts[0]}" — type HELP`, "error");
      return;
    }

    switch (cmd.full) {
      case "WALL":     log("Wall draw mode — click to place endpoints"); onCommand({ type:"MODE", mode:"wall" }); break;
      case "SELECT":   log("Select mode"); onCommand({ type:"MODE", mode:"select" }); break;
      case "MOVE":     log("Move mode — click a node to drag"); onCommand({ type:"MODE", mode:"select" }); break;
      case "ROOM":     log("Room label mode — click inside a closed room"); onCommand({ type:"MODE", mode:"room" }); break;
      case "OPENING":  log("Opening mode — click a wall to place door/window"); onCommand({ type:"MODE", mode:"opening" }); break;
      case "ANNOTATE": log("Annotation mode — click to place text"); onCommand({ type:"MODE", mode:"annotate" }); break;
      case "DIM":      log("Dimension mode — click two points"); onCommand({ type:"MODE", mode:"dim" }); break;
      case "ADIM":     log("Angular dim — click: 1) vertex  2) point on arm A  3) point on arm B"); onCommand({ type:"MODE", mode:"adim" }); break;
      case "HEAL":     log("Healing graph…"); onCommand({ type:"HEAL" }); break;
      case "CLEAR":    log("Canvas cleared", "warn"); onCommand({ type:"CLEAR" }); break;
      case "UNDO":     log("Undo"); onCommand({ type:"UNDO" }); break;
      case "REDO":     log("Redo"); onCommand({ type:"REDO" }); break;

      case "DXF": {
        log("Exporting DXF…", "ok");
        onCommand({ type: "EXPORT_DXF" });
        break;
      }

      case "BOQ": {
        log("Exporting full BOQ CSV…", "ok");
        onCommand({ type: "EXPORT_BOQ" });
        break;
      }

      case "SCHEDULE": {
        const sub = parts[1] || "walls";
        if (sub === "WALLS"   || sub === "WALL")    { log("Exporting wall schedule…",    "ok"); onCommand({ type: "EXPORT_WALL_SCHEDULE" }); }
        else if (sub === "OPENINGS" || sub === "OPEN") { log("Exporting opening schedule…", "ok"); onCommand({ type: "EXPORT_OPENING_SCHEDULE" }); }
        else if (sub === "ROOMS"  || sub === "ROOM")   { log("Exporting room schedule…",    "ok"); onCommand({ type: "EXPORT_ROOM_SCHEDULE" }); }
        else { log("Usage: SCHEDULE walls | openings | rooms", "error"); }
        break;
      }

      case "LSTATE": {
        const sub  = parts[1];
        const name = parts.slice(2).join(" ") || parts[2] || "";
        if (!sub) {
          log("Usage: LSTATE SAVE <name> | RESTORE <name> | DELETE <name> | LIST");
        } else if (sub === "SAVE" && name) {
          log(`Layer state saved: "${name}"`, "ok");
          onCommand({ type: "LAYER_STATE_SAVE", name });
        } else if (sub === "RESTORE" && name) {
          log(`Layer state restored: "${name}"`, "ok");
          onCommand({ type: "LAYER_STATE_RESTORE", name });
        } else if (sub === "DELETE" && name) {
          log(`Layer state deleted: "${name}"`, "warn");
          onCommand({ type: "LAYER_STATE_DELETE", name });
        } else if (sub === "LIST") {
          onCommand({ type: "LAYER_LIST", callback: (layers, layerStates) => {
            const states = Object.keys(layerStates || {});
            if (states.length === 0) log("No saved layer states");
            else states.forEach(n => log(`  • ${n}`));
          }});
        } else {
          log("Usage: LSTATE SAVE <name> | RESTORE <name> | LIST", "error");
        }
        break;
      }

      case "TRIM": {
        log("Trim mode — click edge to trim, then click cutting edge (K key shortcut)", "info");
        onCommand({ type: "MODE", mode: "trim" });
        break;
      }

      case "VIEW3D": {
        log("Toggling 3D preview (3 key shortcut)", "ok");
        onCommand({ type: "TOGGLE_3D" });
        break;
      }

      // ── Geometric primitives ────────────────────────────────────────────────
      case "CIRCLE": {
        // CIRCLE cx,cy r  → direct draw  |  CIRCLE → activate mode
        if (parts[1]) {
          const [cxy, rStr] = [parts[1], parts[2]];
          const [cx, cy]    = (cxy || "0,0").split(",").map(Number);
          const r           = parseFloat(rStr);
          if (!isNaN(cx) && !isNaN(cy) && !isNaN(r) && r > 0) {
            log(`Circle at (${cx}, ${cy}) r=${r}mm`, "ok");
            onCommand({ type: "DRAW_CIRCLE", cx, cy, r });
          } else {
            log("Usage: CIRCLE cx,cy r  e.g.  CIRCLE 0,0 500", "error");
          }
        } else {
          log("Circle mode — click center then radius point (C key shortcut)", "info");
          onCommand({ type: "MODE", mode: "circle" });
        }
        break;
      }

      case "ARC": {
        // ARC cx,cy r startDeg endDeg  → direct  |  ARC → mode
        if (parts[1] && parts[2] && parts[3] && parts[4]) {
          const [cx, cy] = parts[1].split(",").map(Number);
          const r        = parseFloat(parts[2]);
          const s        = parseFloat(parts[3]) * Math.PI / 180;
          const e        = parseFloat(parts[4]) * Math.PI / 180;
          if (!isNaN(cx) && !isNaN(r)) {
            log(`Arc at (${cx},${cy}) r=${r}mm ${parts[3]}°→${parts[4]}°`, "ok");
            onCommand({ type: "DRAW_ARC", cx, cy, r, startAngle: s, endAngle: e });
          } else {
            log("Usage: ARC cx,cy r startDeg endDeg  e.g.  ARC 0,0 200 0 180", "error");
          }
        } else {
          log("Arc mode — 3 clicks: start point, point on arc, end point", "info");
          onCommand({ type: "MODE", mode: "arc" });
        }
        break;
      }

      case "SPLINE": {
        if (parts[1]) {
          // SPLINE x1,y1 x2,y2 ... → draw directly
          const pts = parts.slice(1).map(p => {
            const [x, y] = p.split(",").map(Number);
            return { x, y };
          }).filter(p => !isNaN(p.x) && !isNaN(p.y));
          if (pts.length >= 2) {
            log(`Spline with ${pts.length} points`, "ok");
            onCommand({ type: "DRAW_SPLINE", pts });
          } else {
            log("Usage: SPLINE x1,y1 x2,y2 x3,y3 ...", "error");
          }
        } else {
          log("Spline mode — click points, double-click or Enter to finish", "info");
          onCommand({ type: "MODE", mode: "spline" });
        }
        break;
      }

      case "POLYLINE": {
        if (parts[1]) {
          const pts = parts.slice(1).map(p => {
            const [x, y] = p.split(",").map(Number);
            return { x, y };
          }).filter(p => !isNaN(p.x) && !isNaN(p.y));
          if (pts.length >= 2) {
            log(`Polyline with ${pts.length} points`, "ok");
            onCommand({ type: "DRAW_POLYLINE", pts });
          } else {
            log("Usage: POLYLINE x1,y1 x2,y2 x3,y3 ...", "error");
          }
        } else {
          log("Polyline mode — click points, double-click or Enter to finish", "info");
          onCommand({ type: "MODE", mode: "polyline" });
        }
        break;
      }

      case "HATCH": {
        const patterns = ["ansi31","ansi32","wood","concrete","brick","glass","insulation","earth","metal","plywood","none"];
        const pat = parts[1]?.toLowerCase();
        if (pat && patterns.includes(pat)) {
          log(`Hatch pattern: ${pat}`, "ok");
          onCommand({ type: "SET_HATCH", patternId: pat });
        } else {
          log("Hatch patterns: ansi31 | ansi32 | wood | brick | concrete | glass | insulation | earth | metal | plywood | none");
          if (pat) log(`Unknown pattern: ${pat}`, "error");
        }
        break;
      }

      case "MODE": {
        const modes = ["architectural","mechanical","furniture","electrical","civil","schematic"];
        const m = parts[1]?.toLowerCase();
        if (m && modes.includes(m)) {
          log(`Drawing mode → ${m}`, "ok");
          onCommand({ type: "SET_DRAWING_MODE", mode: m });
        } else {
          log("Modes: architectural | mechanical | furniture | electrical | civil | schematic");
          log("Current mode shown in toolbar dropdown");
          if (m) log(`Unknown mode: ${m}`, "error");
        }
        break;
      }

      case "FILLET": {
        const r = parseFloat(parts[1]);
        if (isNaN(r) || r <= 0) { log("Usage: FILLET r  e.g. FILLET 50  (radius in mm)", "error"); break; }
        log(`Fillet r=${r}mm — select 2 edges first, or select them and run this command`, "info");
        onCommand({ type: "FILLET", radius: r });
        break;
      }

      case "CHAMFER": {
        const d1 = parseFloat(parts[1]);
        const d2 = parts[2] ? parseFloat(parts[2]) : d1;
        if (isNaN(d1) || d1 <= 0) { log("Usage: CHAMFER d  or  CHAMFER d1 d2", "error"); break; }
        log(`Chamfer d=${d1}/${d2}mm`, "info");
        onCommand({ type: "CHAMFER", dist1: d1, dist2: d2 });
        break;
      }

      case "EXTEND": {
        log("Extend mode — click edge to extend, then click boundary edge", "info");
        onCommand({ type: "MODE", mode: "extend" });
        break;
      }

      case "POLYGON": {
        const sides = parseInt(parts[1]);
        const r     = parseFloat(parts[2]);
        const rot   = parts[3] ? parseFloat(parts[3]) : 0;
        if (isNaN(sides) || sides < 3 || isNaN(r) || r <= 0) {
          log("Usage: POLYGON sides r [rotation°]  e.g. POLYGON 6 500", "error"); break;
        }
        log(`Polygon ${sides} sides r=${r}mm at cursor`, "ok");
        onCommand({ type: "POLYGON", sides, r, rotation: rot });
        break;
      }

      case "BREAK": {
        log("Break mode — click an edge to split it at that point", "info");
        onCommand({ type: "MODE", mode: "break" });
        break;
      }

      case "LINETYPE": {
        const types = ["continuous","dashed","dotted","dashdot","hidden"];
        const lt = parts[1]?.toLowerCase();
        if (lt && types.includes(lt)) {
          log(`Linetype → ${lt}`, "ok");
          onCommand({ type: "SET_LINETYPE", entityType: "edge", entityId: null, linetype: lt });
        } else {
          log("Linetypes: continuous | dashed | dotted | dashdot | hidden");
        }
        break;
      }

      case "LINEWEIGHT": {
        const lw = parseFloat(parts[1]);
        if (isNaN(lw)) { log("Usage: LW 0.35  (mm: 0.18/0.25/0.35/0.5/0.7/1.0)", "error"); break; }
        log(`Lineweight → ${lw}mm`, "ok");
        onCommand({ type: "SET_LINEWEIGHT", entityType: "edge", entityId: null, lineweight: lw });
        break;
      }

      case "CNC": {
        const machine = parts[1]?.toLowerCase() || "router";
        const thickness = parts[2] ? parseFloat(parts[2]) : 18;
        const valid = ["router","laser","plasma"];
        if (!valid.includes(machine)) { log("Machines: router | laser | plasma", "error"); break; }
        log(`Exporting G-code for ${machine} (material ${thickness}mm)…`, "ok");
        onCommand({ type: "EXPORT_GCODE", machine, thickness, kerf: true });
        break;
      }

      case "CUTLIST": {
        log("Exporting cut list CSV…", "ok");
        onCommand({ type: "EXPORT_CUTLIST" });
        break;
      }

      case "NEST": {
        const sheet = parts[1] || "8x4";
        log(`Nesting panels on ${sheet} sheet…`, "ok");
        onCommand({ type: "NEST_PANELS", sheet });
        break;
      }

      case "PANEL": {
        const type = parts[1] || "base";
        const w    = parseFloat(parts[2]) || 600;
        const h    = parseFloat(parts[3]) || 720;
        log(`Insert ${type} cabinet module ${w}×${h}mm`, "ok");
        onCommand({ type: "INSERT_CABINET", cabinetType: type, w, h });
        break;
      }

      case "BLOCK": {
        const name = parts.slice(1).join(" ") || "Block";
        log(`Defining block "${name}" from current selection…`, "ok");
        onCommand({ type: "DEFINE_BLOCK", name });
        break;
      }

      case "INSERT": {
        const defId = parts[1];
        if (!defId) {
          log("Usage: INSERT <blockId>  — use BLOCK to list defined blocks", "error");
          break;
        }
        log(`Inserting block ${defId} at cursor…`, "ok");
        onCommand({ type: "INSERT_BLOCK", defId });
        break;
      }

      case "EXPLODE": {
        const refId = parts[1] || null;
        log(`Exploding block reference${refId ? " " + refId : ""}…`, "ok");
        onCommand({ type: "EXPLODE_BLOCK", refId });
        break;
      }

      case "AI": {
        const prompt = parts.slice(1).join(" ");
        if (!prompt) {
          log("Usage: AI <natural language request>", "info");
          log("Examples:");
          log("  AI 3BHK flat 1200 sqft with 2 bathrooms");
          log("  AI ball bearing OD=50 ID=20 6 balls");
          log("  AI TV unit 1800mm wide 3 bays");
          break;
        }
        log(`AI drawing: "${prompt}"…`, "info");
        log("Processing… (Ctrl+J to open AI panel)", "info");
        onCommand({ type: "AI_DRAW", prompt });
        break;
      }

      case "FLOOR": {
        const idx = parseInt(parts[1]);
        if (isNaN(idx) || idx < 0) {
          log("Usage: FLOOR n  (e.g. FLOOR 0 = ground, FLOOR 1 = first)", "error");
          break;
        }
        log(`Switching to floor ${idx}`, "ok");
        onCommand({ type: "SET_FLOOR", floor: idx });
        break;
      }

      case "CON": {
        const sub = parts[1];
        if (!sub || sub === "HELP") {
          log("CON LOCK [mm]     — lock selected wall length");
          log("CON UNLOCK        — remove constraints from selected wall");
          log("CON PERP          — make 2 selected walls perpendicular");
          log("CON PAR           — make 2 selected walls parallel");
          log("CON H             — force selected wall horizontal");
          log("CON V             — force selected wall vertical");
          log("CON ANGLE <deg>   — set angle between 2 selected walls");
          log("CON EQ            — make 2 selected walls equal length");
        } else if (sub === "LOCK") {
          const mm = parts[2] ? parseFloat(parts[2]) : null;
          const mmStr = mm ? ` at ${mm}mm` : " (current length)";
          log(`Locking wall length${mmStr}`, "ok");
          onCommand({ type: "LOCK_LENGTH", length: mm });
        } else if (sub === "UNLOCK") {
          log("Removing constraints from selected wall", "warn");
          onCommand({ type: "UNLOCK_EDGE" });
        } else if (sub === "PERP") {
          log("Making 2 selected walls perpendicular", "ok");
          onCommand({ type: "MAKE_PERPENDICULAR" });
        } else if (sub === "PAR") {
          log("Making 2 selected walls parallel", "ok");
          onCommand({ type: "MAKE_PARALLEL" });
        } else if (sub === "H") {
          log("Forcing wall horizontal", "ok");
          onCommand({ type: "MAKE_HORIZONTAL" });
        } else if (sub === "V") {
          log("Forcing wall vertical", "ok");
          onCommand({ type: "MAKE_VERTICAL" });
        } else if (sub === "ANGLE") {
          const deg = parseFloat(parts[2]);
          if (isNaN(deg)) { log("Usage: CON ANGLE <degrees>", "error"); break; }
          log(`Setting angle to ${deg}°`, "ok");
          onCommand({ type: "SET_ANGLE", angle: deg });
        } else if (sub === "EQ" || sub === "EQUAL") {
          log("Making 2 selected walls equal length", "ok");
          onCommand({ type: "MAKE_EQUAL_LENGTH" });
        } else {
          log("Unknown constraint. Type CON for help.", "error");
        }
        break;
      }

      case "ZOOM": {
        const factor = parseFloat(parts[1]);
        if (isNaN(factor) || factor <= 0) { log("Usage: ZOOM 2  (or any positive number)", "error"); break; }
        log(`Zoom → ${factor}×`);
        onCommand({ type:"ZOOM", zoom: factor });
        break;
      }

      case "LAYER": {
        const sub = parts[1], id = parts[2]?.toLowerCase();
        if (!sub) {
          log("Usage: LAYER SHOW <id>  |  LAYER HIDE <id>  |  LAYER LIST");
          onCommand({ type:"LAYER_LIST" });
        } else if (sub === "SHOW" && id) {
          log(`Layer visible: ${id}`);
          onCommand({ type:"LAYER_VISIBLE", layerId: id, visible: true });
        } else if (sub === "HIDE" && id) {
          log(`Layer hidden: ${id}`, "warn");
          onCommand({ type:"LAYER_VISIBLE", layerId: id, visible: false });
        } else if (sub === "LIST") {
          onCommand({ type:"LAYER_LIST", callback: (layers) => {
            if (layers) {
              Object.values(layers).forEach(l => {
                log(`  ${l.visible?"●":"○"} ${l.locked?"🔒":" "} ${l.id.padEnd(14)} ${l.name}`, l.visible ? "info" : "warn");
              });
            }
          }});
        } else {
          log("Usage: LAYER SHOW <id>  |  LAYER HIDE <id>  |  LAYER LIST", "error");
        }
        break;
      }

      case "LINE": {
        // LINE x1,y1 x2,y2
        const parse = s => { const [x,y] = s.split(',').map(Number); return {x,y}; };
        try {
          const ptA = parse(parts[1]), ptB = parse(parts[2]);
          if (isNaN(ptA.x)||isNaN(ptA.y)||isNaN(ptB.x)||isNaN(ptB.y)) throw new Error();
          log(`Wall: (${ptA.x},${ptA.y}) → (${ptB.x},${ptB.y}) mm`);
          onCommand({ type:"DRAW_WALL", ptA, ptB });
        } catch {
          log("Usage: LINE x1,y1 x2,y2  e.g. LINE 0,0 5000,0", "error");
        }
        break;
      }

      case "RECT": {
        // RECT x,y w,h  — draws 4 walls forming a rectangle
        try {
          const [ox,oy] = parts[1].split(',').map(Number);
          const [rw,rh] = parts[2].split(',').map(Number);
          if ([ox,oy,rw,rh].some(isNaN)) throw new Error();
          log(`Rectangle: origin(${ox},${oy}) size(${rw}×${rh}) mm`);
          onCommand({ type:"DRAW_RECT", x:ox, y:oy, w:rw, h:rh });
        } catch {
          log("Usage: RECT x,y width,height  e.g. RECT 0,0 6000,4000", "error");
        }
        break;
      }

      case "GRID": {
        // GRID X label  or  GRID Y label
        const axis  = parts[1] === "Y" ? "y" : "x";
        const label = parts[2] || (axis === "x" ? "A" : "1");
        const pos   = snapPoint ? (axis === "x" ? snapPoint.x : snapPoint.y) : 0;
        log(`Grid line ${axis.toUpperCase()}=${pos}mm labelled "${label}"`);
        onCommand({ type:"GRID_LINE", axis, position: pos, label });
        break;
      }

      case "COPY":
      case "CP": {
        // COPY dx,dy  — copy selected edges offset by dx,dy mm
        try {
          const [dx, dy] = (parts[1] || "0,0").split(",").map(Number);
          if (isNaN(dx) || isNaN(dy)) throw new Error();
          log(`Copy → offset (${dx}, ${dy}) mm`);
          onCommand({ type: "COPY_EDGES", dx, dy });
        } catch {
          log("Usage: CP dx,dy  e.g. CP 3000,0", "error");
        }
        break;
      }

      case "ROTATE":
      case "RO": {
        const angle = parseFloat(parts[1]);
        if (isNaN(angle)) { log("Usage: RO <angle_deg>  e.g. RO 90", "error"); break; }
        log(`Rotate ${angle}°`);
        onCommand({ type: "ROTATE", angle });
        break;
      }

      case "SCALE":
      case "SC": {
        const factor = parseFloat(parts[1]);
        if (isNaN(factor) || factor <= 0) { log("Usage: SC <factor>  e.g. SC 2", "error"); break; }
        log(`Scale ×${factor}`);
        onCommand({ type: "SCALE", factor });
        break;
      }

      case "MIRROR":
      case "MI": {
        log("Mirror mode — click two points to define the mirror axis (I key shortcut)");
        onCommand({ type: "MIRROR_MODE" });
        break;
      }

      case "ARRAY":
      case "AR": {
        // AR cols rows colSpacing rowSpacing
        try {
          const cols = parseInt(parts[1]) || 2;
          const rows = parseInt(parts[2]) || 2;
          const cs   = parseFloat(parts[3]) || 3000;
          const rs   = parseFloat(parts[4]) || 3000;
          log(`Array ${cols}×${rows} @ (${cs}mm, ${rs}mm)`);
          onCommand({ type: "ARRAY_RECT", cols, rows, colSpacing: cs, rowSpacing: rs });
        } catch {
          log("Usage: AR cols rows colSpacing rowSpacing  e.g. AR 3 2 3000 3000", "error");
        }
        break;
      }

      case "ARRAYPOLAR":
      case "AP": {
        const count = parseInt(parts[1]) || 4;
        const arc   = parseFloat(parts[2]) || 360;
        log(`Polar array ×${count} over ${arc}°`);
        onCommand({ type: "ARRAY_POLAR", count, totalAngle: arc });
        break;
      }

      case "OFFSET":
      case "OF": {
        const dist = parts[1] ? Math.abs(parseFloat(parts[1])) : 0;
        if (!dist || isNaN(dist)) {
          log("Usage: OFFSET <distance_mm>  e.g. OFFSET 500", "error");
          log("Then click an edge to create a parallel copy at that distance.");
        } else {
          log(`Offset mode — ${dist}mm · click an edge (F key shortcut)`, "info");
          onCommand({ type: "OFFSET", dist });
        }
        break;
      }

      case "HELP": {
        log("─── v8 Commands ────────────────────────────");
        log("W Wall  S Select  C Circle  ARC  SPL  PL");
        log("F Offset  K Trim  EX Extend  BR Break");
        log("FILLET r   CHAMFER d   POLYGON sides r");
        log("RO°  SC×  CP  AR  AP  I Mirror");
        log("BLOCK name  INSERT defId  EXPLODE [refId]");
        log("AI <prompt>  — draw from natural language");
        log("FLOOR n  — switch floor (0=ground, 1=first)");
        log("CON LOCK|PERP|PAR|H|V|ANGLE|EQ");
        log("HATCH wood|brick|concrete|glass|metal");
        log("LT linetype   LW lineweight");
        log("MODE architectural|mechanical|furniture");
        log("MODE electrical|civil|schematic");
        log("CNC router|laser   CUTLIST   NEST 8x4");
        log("PANEL base|wall|tall|drawer|tv_unit w h");
        log("DXF  BOQ  SCHEDULE  3 → 3D  Ctrl+J → AI");
        log("H Heal  U Undo  REDO  C Clear  P Transform");
        log("────────────────────────────────────────────");
        break;
      }
      default: break;
    }
  }, [onCommand, log, snapPoint]);

  const onKeyDown = useCallback(e => {
    if (e.key === "Enter") {
      execute(input);
      setInput("");
      setSuggest([]);
    } else if (e.key === "Escape") {
      setInput("");
      setSuggest([]);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHistIdx(i => {
        const next = Math.min(i + 1, cmdHist.length - 1);
        if (cmdHist[next] !== undefined) setInput(cmdHist[next]);
        return next;
      });
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHistIdx(i => {
        const next = Math.max(i - 1, -1);
        setInput(next >= 0 ? cmdHist[next] : "");
        return next;
      });
    } else if (e.key === "Tab" && suggest.length > 0) {
      e.preventDefault();
      setInput(suggest[0] + " ");
      setSuggest([]);
    }
  }, [input, execute, cmdHist, suggest]);

  // Expose log function for external use
  useEffect(() => {
    if (onCommand._log) onCommand._log = log;
  }, [log, onCommand]);

  const LOG_COLORS = {
    cmd:   "#e8a838",
    info:  "#4a5e70",
    warn:  "#fb923c",
    error: "#f87171",
    ok:    "#4ade80",
  };

  return (
    <div style={{
      height: 160, background: "#030508", borderTop: "1px solid #0d1520",
      display: "flex", flexDirection: "column", flexShrink: 0,
      fontFamily: "'JetBrains Mono',monospace",
    }}>
      {/* Log area */}
      <div ref={logRef} style={{
        flex: 1, overflowY: "auto", padding: "4px 12px",
        scrollbarWidth: "thin", scrollbarColor: "#0d1520 transparent",
      }}>
        {history.map((h, i) => (
          <div key={i} style={{
            fontSize: 10.5, lineHeight: 1.6,
            color: LOG_COLORS[h.type] || "#4a5e70",
            whiteSpace: "pre-wrap",
          }}>{h.text}</div>
        ))}
      </div>

      {/* Autocomplete suggestions */}
      {suggest.length > 0 && (
        <div style={{ padding: "2px 12px", display: "flex", gap: 6, borderTop: "1px solid #0d1520" }}>
          {suggest.map(s => (
            <button key={s} onClick={() => { setInput(s + " "); setSuggest([]); inputRef.current?.focus(); }}
              style={{ background: "#0c1320", border: "1px solid #1e2a36", color: "#3b82f6",
                padding: "1px 8px", borderRadius: 3, fontSize: 9.5, cursor: "pointer", fontFamily: "inherit" }}>
              {s}
            </button>
          ))}
          <span style={{ fontSize: 9, color: "#1e2a36", marginLeft: 2, alignSelf: "center" }}>Tab to complete</span>
        </div>
      )}

      {/* Input row */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "5px 12px", borderTop: "1px solid #0d1520",
      }}>
        {/* Mode indicator */}
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: 1,
          color: "#e8a838", minWidth: 72,
          background: "#0c1320", padding: "2px 8px", borderRadius: 3,
          border: "1px solid #1e2a36",
        }}>
          {mode?.toUpperCase() || "SELECT"}
        </span>

        {/* Coords */}
        {snapPoint && (
          <span style={{ fontSize: 9.5, color: "#2a3a4a", minWidth: 130 }}>
            X:<span style={{ color: "#22c55e" }}>{Math.round(snapPoint.x)}</span>
            {" "}Y:<span style={{ color: "#22c55e" }}>{Math.round(snapPoint.y)}</span>
            <span style={{ color: "#1e2a36" }}> mm</span>
          </span>
        )}

        <span style={{ color: "#1e2a36", fontSize: 12, userSelect: "none" }}>›</span>

        <input
          ref={inputRef}
          value={input}
          onChange={e => { setInput(e.target.value.toUpperCase()); handleSuggest(e.target.value); }}
          onKeyDown={onKeyDown}
          spellCheck={false}
          autoComplete="off"
          placeholder="Type a command or press ? for help…"
          style={{
            flex: 1, background: "transparent", border: "none",
            color: "#e8a838", fontSize: 11, fontFamily: "inherit",
            outline: "none", caretColor: "#e8a838",
            letterSpacing: 0.5,
          }}
        />

        <span style={{ fontSize: 9, color: "#1e2a36" }}>{Math.round(zoom * 100)}%</span>
      </div>
    </div>
  );
}
